import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography, alpha } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockClockRoundedIcon from '@mui/icons-material/LockClockRounded';
import { usePayOrderSalaryData } from 'entities/salary/salary-entries/salary-entries.hook';
import { POS_CONFIRMED, POS_DRAFT, POS_STATUSES, PayOrderStatus } from '@sr/dto';
import { salaryEntriesApi } from 'entities/salary/salary-entries/salary-entries.api';
import { fetchPayrollPrintForm } from 'features/wcc/wcc-print-form/api';
import { usePrintPopup } from 'utils/print-popup.hook';
import { ReactNode, useCallback } from 'react';
import { useSnack } from 'shared/ui/snack';
import PriceSpan from 'shared/ui/price-span';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { formatDateMonthYear } from 'utils/date-format-helpers';

interface Props {
    year: number;
    month: number;
    userId: number;
}

export const Payroll = ({ year, month, userId }: Props) => {
  const { collection, isLoading, error } = usePayOrderSalaryData(year, month, userId);
  const [closePayroll] = salaryEntriesApi.useCreatePayOrderWagesMutation();
  const [openPayroll] = salaryEntriesApi.useDeletePayOrderWagesMutation();

  const {  showSuccess, showError } = useSnack();

  const { openWindow } = usePrintPopup(useCallback(() => {
    if (!collection?.salaryEntry?.id) {
      showError('ID salaryEntry не найден');
      return Promise.resolve('');
    }
    return fetchPayrollPrintForm(collection.salaryEntry.id)
      .catch(e => {
        showError(`Ошибка получения печатной формы: ${e}`);
        return '';
      });
  }, [collection?.salaryEntry?.id, showError]));

  const handleClosePeriod = async () => {
    try {
      if (!collection?.salaryEntry?.id) {
        showError('Не удалось найти запись о закрытии табеля');
        return;
      }
      await closePayroll(collection.salaryEntry.id).unwrap();
      showSuccess('Заработная плата закрыта');
    } catch (error: any) {
      showError(`Ошибка закрытия ведомости: ${error.data.message}`);
    }
  };

  const handleOpenPeriod = async () => {
    try {
      if (!collection?.salaryEntry?.id) {
        showError('Не удалось найти запись о закрытии табеля');
        return;
      }
      await openPayroll(collection.salaryEntry.id).unwrap();
      showSuccess('Заработная плата открыта');
    }
    catch (error: any) {
      showError(`Ошибка открытия ведомости: ${error.data.message}`);
    }
  };

  if (isLoading) return <StatusPanel tone="neutral"><LoadingBanner /></StatusPanel>;
  if (error) return <StatusPanel tone="error"><Typography color="error">Ошибка: {error}</Typography></StatusPanel>;

  const salaryEntry = collection?.salaryEntry;
  const payOrder = collection?.payOrder;

  if (!salaryEntry) {
    return (
      <StatusPanel tone="neutral" icon={<LockClockRoundedIcon fontSize="small" color="disabled" />}>
        <Typography variant="subtitle2" color="text.secondary">
          Зарплата за период не сформирована
        </Typography>
      </StatusPanel>
    );
  }

  const isClosed = !!collection?.isClosed;
  const total = collection?.total ?? 0;
  const carryForwardAmount = Math.max(0, -total);
  const nextPeriod = formatDateMonthYear(year + (month === 11 ? 1 : 0), (month + 1) % 12);

  return (
    <StatusPanel
      tone={isClosed ? 'success' : 'warning'}
      icon={
        isClosed
          ? <CheckCircleRoundedIcon fontSize="small" color="success" />
          : <LockClockRoundedIcon fontSize="small" color="warning" />
      }
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          {isClosed && (
            <Tooltip title="Печать ведомости">
              <IconButton onClick={openWindow} color="primary" size="small">
                <PrintIcon />
              </IconButton>
            </Tooltip>
          )}
          {isClosed && (!payOrder || payOrder.status === POS_DRAFT || payOrder.status === POS_CONFIRMED) && (
            <Button variant="outlined" startIcon={<LockOpenIcon />} onClick={handleOpenPeriod} color="success" size="small">
              Открыть
            </Button>
          )}
          {!isClosed && (
            <Button variant="contained" startIcon={<LockIcon />} onClick={handleClosePeriod} color="warning" size="small">
              Закрыть зарплату
            </Button>
          )}
        </Stack>
      }
    >
      <Stack spacing={0.25}>
        <Typography variant="subtitle1" fontWeight={600}>
          {isClosed ? 'Зарплата закрыта' : 'Зарплата открыта'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5, rowGap: 0.5 }}>
          <Typography variant="body2" color="text.secondary" component="span">
            К выдаче:
          </Typography>
          <Box component="span">
            <PriceSpan price={Math.max(0, total)} coloring size="regular" />
          </Box>
          {isClosed && payOrder && (
            <>
              <Typography variant="body2" color="text.secondary" component="span">
                Расходный ордер №&nbsp;<b>{payOrder.documentNumber || '—'}</b>
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={payOrder.status === POS_DRAFT ? 'default' : 'success'}
                label={POS_STATUSES[payOrder.status as PayOrderStatus] ?? '—'}
              />
            </>
          )}
        </Box>
        {carryForwardAmount > 0 && (
          <Typography variant="body2" color="text.secondary" component="div">
            Отрицательный остаток: <PriceSpan price={carryForwardAmount} size="small" />.
            {' '}{isClosed ? 'Перенесён' : 'При закрытии зарплаты будет перенесён'} в следующий период ({nextPeriod}) в виде удержания.
          </Typography>
        )}
      </Stack>
    </StatusPanel>
  );
};

type Tone = 'success' | 'warning' | 'neutral' | 'error';

const StatusPanel = ({
  tone,
  icon,
  actions,
  children,
}: {
  tone: Tone;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) => {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        my: 2,
        px: 2,
        py: 1.5,
        borderColor: tone === 'neutral' ? 'divider' : theme.palette[tone].light,
        backgroundColor:
          tone === 'neutral'
            ? theme.palette.action.hover
            : alpha(theme.palette[tone].main, 0.08),
        borderRadius: 2,
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          {icon}
          <Box sx={{ minWidth: 0 }}>{children}</Box>
        </Stack>
        {actions}
      </Stack>
    </Paper>
  );
};
